import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

interface DialogRegistration {
  host: HTMLElement;
  suspended: boolean;
}

interface OriginalInertState {
  hadAttribute: boolean;
  value: string | null;
}

/** Keeps everything outside the top active inline dialog inert. */
@Injectable({ providedIn: 'root' })
export class AstralDialogBackgroundInertService {
  private readonly dialogs: DialogRegistration[] = [];
  private readonly managedElements = new Map<HTMLElement, OriginalInertState>();

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  register(host: HTMLElement, suspended: boolean): void {
    const registration = this.dialogs.find(dialog => dialog.host === host);
    if (registration) {
      registration.suspended = suspended;
    } else {
      this.dialogs.push({ host, suspended });
    }

    this.updateBackground();
  }

  unregister(host: HTMLElement): void {
    const index = this.dialogs.findIndex(dialog => dialog.host === host);
    if (index !== -1) {
      this.dialogs.splice(index, 1);
      this.updateBackground();
    }
  }

  private updateBackground(): void {
    this.restoreManagedElements();
    let activeDialog: DialogRegistration | undefined;
    for (let index = this.dialogs.length - 1; index >= 0; index -= 1) {
      const dialog = this.dialogs[index];
      if (!dialog.suspended && dialog.host.isConnected) {
        activeDialog = dialog;
        break;
      }
    }
    if (!activeDialog) {
      return;
    }

    let protectedElement: HTMLElement = activeDialog.host;
    while (protectedElement.parentElement) {
      const parent = protectedElement.parentElement;
      for (const sibling of Array.from(parent.children)) {
        if (sibling !== protectedElement && sibling instanceof HTMLElement) {
          this.makeInert(sibling);
        }
      }

      if (parent === this.document.body) {
        break;
      }
      protectedElement = parent;
    }
  }

  private makeInert(element: HTMLElement): void {
    if (!this.managedElements.has(element)) {
      this.managedElements.set(element, {
        hadAttribute: element.hasAttribute('inert'),
        value: element.getAttribute('inert'),
      });
    }
    element.setAttribute('inert', '');
  }

  private restoreManagedElements(): void {
    for (const [element, originalState] of this.managedElements) {
      if (originalState.hadAttribute) {
        element.setAttribute('inert', originalState.value ?? '');
      } else {
        element.removeAttribute('inert');
      }
    }
    this.managedElements.clear();
  }
}
